import React from 'react';

import { InformationDataType } from '@components/widgets/information-box';
import InformationBox from '@components/widgets/information-box/InformationBox';
import { MAIN_THEME } from '@styles';
import { valueCommaSeparated } from '@utils/formatting-utils';
import { getGenericLine } from '@utils/helper-utils';
import { GenericLines } from 'src/enums';

import {
  AmendmentDetails,
  AmendmentOfferDetails
} from '../types/getAmendmentsDetailsResponse.types';

import DateService from 'src/core/services/DateService';
import FormattingService from 'src/core/services/FormattingService';
import { makeStyles } from 'tss-react/mui';

type Props = { data: AmendmentDetails };

const useStyles = makeStyles()(() => ({
  container: { width: '100%' },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    alignContent: 'center',
    justifyContent: 'space-between'
  },
  title: {
    color: MAIN_THEME.color.black,
    fontSize: MAIN_THEME.fontSize.sm,
    lineHeight: '17px',
    display: 'block',
    textAlign: 'left'
  },
  detailContainer: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignContent: 'center',
    flexWrap: 'wrap',
    paddingTop: '10px'
  },
  mainAmountValue: {
    color: MAIN_THEME.color.black,
    fontSize: MAIN_THEME.fontSize.lg,
    lineHeight: '20px',
    float: 'left',
    fontWeight: 'bold'
  },
  premiumAmount: {
    color: MAIN_THEME.color.red,
    fontSize: MAIN_THEME.fontSize.lg,
    lineHeight: '20px',
    fontWeight: 'bold',
    float: 'right'
  }
}));

export const offerDetailsN = (v: any): number => {
  const num = Number(v ?? 0);

  return Number.isFinite(num) ? num : 0;
};

export const getOfferDetailsSym = (...xs: Array<string | undefined>): string =>
  xs.find((s) => !!s)?.toString() ?? '';

const readChargesAmount = (s: any) => s?.chargesAmount ?? s?.charges ?? 0;
const readTpaFeesTotalAmount = (s: any) =>
  s?.tpaFeesTotalAmount ?? s?.tpaFees ?? 0;
const readFixedStampAmount = (s: any) =>
  s?.fixedStampAmount ?? s?.fixedStamp ?? 0;
const readProportionalStampAmount = (s: any) =>
  s?.proportionalStampAmount ?? s?.proportionalStamp ?? 0;
const readMunicipalityTaxAmount = (s: any) =>
  s?.municipalityTaxAmount ?? s?.municipalityTax ?? 0;

export const buildOfferDetailsBreakdownData = (
  src?: AmendmentOfferDetails | null,
  currencySymbol?: string
) => {
  if (!src) return {};

  return {
    BasicPremium: offerDetailsN((src as any).basicPremium),
    NetPremium: offerDetailsN((src as any).netPremium),
    PolicyCostAmount: offerDetailsN((src as any).policyCost),
    Charges: offerDetailsN(readChargesAmount(src)),
    TPAFees: offerDetailsN(readTpaFeesTotalAmount(src)),
    GrossPremium: offerDetailsN((src as any).grossPremium),
    FixedStamp: offerDetailsN(readFixedStampAmount(src)),
    ProportionalStamp: offerDetailsN(readProportionalStampAmount(src)),
    MunicipalityTax: offerDetailsN(readMunicipalityTaxAmount(src)),
    TotalPremium: offerDetailsN((src as any).totalPremium),
    __currency: currencySymbol || (src as any)?.currency?.symbol || ''
  };
};

export const offerDetailsBreakdownHeaders = (currencySymbol: string) => ({
  BasicPremium: {
    name: 'BasicPremium',
    title: 'Basic Premium',
    type: InformationDataType.RoundedCurrency,
    currencySymbol
  },
  NetPremium: {
    name: 'NetPremium',
    title: 'Net Premium',
    type: InformationDataType.RoundedCurrency,
    currencySymbol
  },
  PolicyCostAmount: {
    name: 'PolicyCostAmount',
    title: 'Policy Cost',
    type: InformationDataType.RoundedCurrency,
    currencySymbol
  },
  Charges: {
    name: 'Charges',
    title: 'Charges',
    type: InformationDataType.RoundedCurrency,
    currencySymbol
  },
  TPAFees: {
    name: 'TPAFees',
    title: 'TPA Fees',
    type: InformationDataType.RoundedCurrency,
    currencySymbol
  },
  GrossPremium: {
    name: 'GrossPremium',
    title: 'Gross Premium',
    type: InformationDataType.RoundedCurrency,
    currencySymbol
  },
  FixedStamp: {
    name: 'FixedStamp',
    title: 'Fixed Stamp',
    type: InformationDataType.RoundedCurrency,
    currencySymbol
  },
  ProportionalStamp: {
    name: 'ProportionalStamp',
    title: 'Proportional Stamp',
    type: InformationDataType.RoundedCurrency,
    currencySymbol
  },
  MunicipalityTax: {
    name: 'MunicipalityTax',
    title: 'Municipality Tax',
    type: InformationDataType.RoundedCurrency,
    currencySymbol
  },
  TotalPremium: {
    name: 'TotalPremium',
    title: 'Total Premium',
    type: InformationDataType.RoundedCurrency,
    currencySymbol
  }
});

const OfferDetailsEndorsementWidgetAtom: React.FC<Props> = ({ data }) => {
  const { classes } = useStyles();
  const amendmentType = data.amendmentAmendmentDetails.amendmentType;
  const currentPQ = (data as any)?.currentOfferDetails as
    | AmendmentOfferDetails
    | undefined;
  const variance = (data as any)?.policyQuoteVariance as
    | AmendmentOfferDetails
    | undefined;
  const newPQ = (data as any)?.offerDetails as
    | AmendmentOfferDetails
    | undefined;
  const policySym =
    (data as any)?.policyDetails?.currency?.symbol ||
    (data as any)?.summaryDetails?.currency?.symbol ||
    '';

  const symCur = getOfferDetailsSym(currentPQ?.currency?.symbol, policySym);
  const symVar = getOfferDetailsSym(variance?.currency?.symbol, policySym);
  const symNew = getOfferDetailsSym(newPQ?.currency?.symbol, policySym);
  const headerCurrencySymbol = getOfferDetailsSym(
    symNew,
    policySym,
    symCur,
    symVar
  );

  const headerCreatedOn = newPQ?.createdOn
    ? DateService.formatDate(newPQ.createdOn)
    : '';
  const headerStatus = FormattingService.formatEnumToDisplay(
    newPQ?.status ?? ''
  );
  const headerSumInsured = `${symNew} ${valueCommaSeparated(
    offerDetailsN(newPQ?.sumInsured).toFixed(0)
  )}`;
  const headerTotalPremium = `${symNew} ${valueCommaSeparated(
    offerDetailsN(newPQ?.totalPremium).toFixed(0)
  )}`;

  const currentBreakdown = buildOfferDetailsBreakdownData(currentPQ, symCur);
  const varianceBreakdown = buildOfferDetailsBreakdownData(variance, symVar);
  const newBreakdown = buildOfferDetailsBreakdownData(newPQ, symNew);

  const isPolicyCancellation = amendmentType === 'PolicyCancellation';

  // Conditionally build comparison data and grouping values
  const comparisonData = isPolicyCancellation
    ? { Current: currentBreakdown, Variance: varianceBreakdown }
    : {
        Current: currentBreakdown,
        Variance: varianceBreakdown,
        New: newBreakdown
      };

  const groupingValues = isPolicyCancellation
    ? ['Current', 'Variance']
    : ['Current', 'Variance', 'New'];

  const isGroupMedical =
    getGenericLine(String(data?.summaryDetails?.lineCode ?? '')) ===
    GenericLines.MEDICAL_GROUP;
  const widgetTitle = isGroupMedical ? 'Group Offer Details' : 'Offer Details';

  return (
    <InformationBox
      visibleRows={0}
      title={widgetTitle}
      status={headerStatus}
      statusReason=""
      statusIcon=""
      statusColor=""
      reviewedOn=""
      subtitle={`Created On: ${headerCreatedOn}`}
      header={
        <div className={classes.container}>
          <div className={classes.titleContainer}>
            <span className={classes.title}>Coverage</span>
            <span className={classes.title}>Total Premium</span>
          </div>
          <div className={classes.detailContainer}>
            <span className={classes.mainAmountValue}>{headerSumInsured}</span>
            <span className={classes.premiumAmount}>{headerTotalPremium}</span>
          </div>
        </div>
      }
      tabs={
        [
          {
            title: 'Comparison',
            name: 'Comparison',
            data: comparisonData,
            headers: offerDetailsBreakdownHeaders(headerCurrencySymbol),
            supportsGrouping: true,
            groupingValues: groupingValues,
            groupingTitle: null,
            supportCycleDivision: false
          }
        ] as any[]
      }
      collapsible={false}
      initiallyCollapsed={false}
    />
  );
};

export { OfferDetailsEndorsementWidgetAtom };
